// components/user/lead/RescheduleDrawer.jsx
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Drawer,
  Form,
  Select,
  DatePicker,
  Input,
  Divider,
  Button,
  Alert,
  Spin,
} from "antd";
import {
  RocketOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { FaSun, FaMoon } from "react-icons/fa";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { rescheduleServiceRequest, fetchCancelRescheduleReasons } from "../../../store/slices/serviceRequestSlice";
import { IS_DEVELOPMENT } from "../../../utils/constant";

const { Option } = Select;
const { TextArea } = Input;

const RescheduleDrawer = ({
  visible,
  order,
  onClose,
  onSuccess,
  isDarkMode,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [form] = Form.useForm();

  const timeSlots = [
    {
      value: "10:00-14:00",
      label: "10:00 AM - 2:00 PM",
      icon: <FaSun size={12} />,
    },
    {
      value: "14:00-20:00",
      label: "2:00 PM - 8:00 PM",
      icon: <FaMoon size={12} />,
    },
  ];

  // Fetch reschedule reasons when drawer opens
  useEffect(() => {
    if (visible) {
      loadReasons();
    }
  }, [visible]);

  const loadReasons = async () => {
    try {
      setReasonsLoading(true);
      const result = await dispatch(fetchCancelRescheduleReasons({ type: 're_scheduled' })).unwrap();
      setReasons(result || []);
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error("Failed to load reschedule reasons:", error);
      }
    } finally {
      setReasonsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!values.reasonId) {
        toast.error("Please select a reason for reschedule");
        return;
      }

      setLoading(true);

      const payload = {
        slot: values.slot,
        spd: values.spd ? values.spd.format("YYYY-MM-DD") : null,
        reasonId: values.reasonId,
        remark: values.remark,
      };

      const result = await dispatch(
        rescheduleServiceRequest({
          serviceId: order.service_id,
          payload,
        }),
      ).unwrap();

      if (result) {
        toast.success("Service rescheduled successfully");
        form.resetFields();
        onClose();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error("Reschedule failed:", error);
      }
      toast.error(error || "Failed to reschedule service");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const currentSchedule = () => {
    if (order?.is_express) {
      return { type: "Express Pickup", value: "Immediate assignment" };
    }
    if (order?.scheduling?.spd && order?.scheduling?.slot) {
      return {
        type: "Current Schedule",
        value: `${dayjs(order.scheduling.spd).format("DD MMM")} • ${order.scheduling.slot}`,
      };
    }
    return null;
  };

  const scheduleInfo = currentSchedule();

  return (
    <Drawer
      title={
        <div className="flex items-center gap-2">
          {order?.is_express ? (
            <RocketOutlined className="text-base text-orange-500" />
          ) : (
            <CalendarOutlined className="text-base text-blue-500" />
          )}
          <span className="text-sm font-semibold">
            Reschedule - {order?.service_no}
          </span>
        </div>
      }
      placement="right"
      onClose={handleClose}
      open={visible}
      width={400}
      closable={false}
      footer={null}
      className="reschedule-drawer"
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto">
          {scheduleInfo && (
            <div
              className={`p-3 mb-4 rounded-lg ${isDarkMode ? "bg-gray-800" : "bg-blue-50"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <ClockCircleOutlined
                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-blue-500"}`}
                />
                <span className="text-xs font-medium">Current Schedule</span>
              </div>
              <div className="text-xs">{scheduleInfo.value}</div>
            </div>
          )}

          {order?.is_express && (
            <Alert
              message="Express Pickup"
              description="Pickup will be processed immediately. A partner will be assigned within 30 minutes."
              type="info"
              showIcon
              icon={<RocketOutlined />}
              className={`mb-4 text-sm rounded-lg ${isDarkMode ? "bg-gray-800" : "bg-blue-50"}`}
            />
          )}

          <Form
            form={form}
            layout="vertical"
            size="middle"
          >
            <Form.Item
              name="spd"
              label="New Date"
              rules={[{ required: true, message: "Please select a date" }]}
              className="mb-3"
            >
              <DatePicker
                className="w-full rounded-md"
                format="DD MMM YYYY"
                disabledDate={(current) =>
                  current && current < dayjs().startOf("day")
                }
                placeholder="Select date"
                style={{ width: "100%", height: "36px" }}
              />
            </Form.Item>

            <Form.Item
              name="slot"
              label="New Time Slot"
              rules={[{ required: true, message: "Please select a time slot" }]}
              className="mb-3"
            >
              <Select
                placeholder="Select time slot"
                className="w-full rounded-md"
                style={{ height: "36px" }}
              >
                {timeSlots.map((slot) => (
                  <Option key={slot.value} value={slot.value}>
                    <div className="flex items-center gap-2">
                      {slot.icon}
                      <span>{slot.label}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Divider className="my-3" />

            <Form.Item
              name="reasonId"
              label="Reason"
              rules={[{ required: true, message: "Please select a reason" }]}
              className="mb-3"
            >
              <Select
                placeholder="Select reason"
                className="w-full rounded-md"
                allowClear={false}
                style={{ height: "36px" }}
                loading={reasonsLoading}
                notFoundContent={reasonsLoading ? <Spin size="small" /> : "No reasons available"}
              >
                {reasons.map((reason) => (
                  <Option key={reason.id} value={reason.id}>
                    {reason.message}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="remark" label="Remark" className="mb-3">
              <TextArea
                rows={2}
                placeholder="Optional remarks"
                className="text-sm"
                style={{ padding: "8px 12px" }}
              />
            </Form.Item>
          </Form>
        </div>

        <div
          className={`pt-3 mt-2 border-t ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}
        >
          <div className="flex gap-2">
            <Button
              onClick={handleClose}
              disabled={loading}
              className="flex-1 text-sm rounded-md h-9"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              icon={<CalendarOutlined />}
              className="flex-1 text-sm rounded-md h-9"
            >
              {loading ? "Rescheduling..." : "Confirm Reschedule"}
            </Button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .reschedule-drawer :global(.ant-select-selector) {
          padding: 4px 11px !important;
          height: 36px !important;
        }
        .reschedule-drawer :global(.ant-input) {
          padding: 8px 12px !important;
        }
        .reschedule-drawer :global(.ant-picker) {
          padding: 4px 11px !important;
          height: 36px !important;
        }
      `}</style>
    </Drawer>
  );
};

export default RescheduleDrawer;